import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type AccountSummaryPaymentMethodItem,
  type AccountSummarySavedCardItem,
} from '@assets/mockdata/PatientTray/mockAccountSummary'
import usePatientProfilePaymentMethods from './usePatientProfilePaymentMethods'

type PendingPaymentDelete =
  | { kind: 'payment'; item: AccountSummaryPaymentMethodItem }
  | { kind: 'card'; item: AccountSummarySavedCardItem }

type UsePatientProfilePaymentMethodActionsOptions = {
  enabled?: boolean
}

export type UsePatientProfilePaymentMethodActions = {
  paymentMethods: AccountSummaryPaymentMethodItem[]
  savedCards: AccountSummarySavedCardItem[]
  isLoading: boolean
  isDeleting: boolean
  isSettingDefault: boolean
  isPaymentNetworkBusy: boolean
  showDeleteModal: boolean
  deleteModalTitle: string
  deleteModalBody?: string
  handleRequestDeletePaymentMethod: (
    item: AccountSummaryPaymentMethodItem,
  ) => void
  handleRequestDeleteSavedCard: (item: AccountSummarySavedCardItem) => void
  handleCloseDeleteModal: () => void
  handleConfirmDelete: () => Promise<void>
  handleSetDefaultPaymentMethod: (item: AccountSummaryPaymentMethodItem) => void
  handleSetDefaultSavedCard: (item: AccountSummarySavedCardItem) => void
}

const usePatientProfilePaymentMethodActions = (
  options: UsePatientProfilePaymentMethodActionsOptions = {},
): UsePatientProfilePaymentMethodActions => {
  const { enabled = true } = options

  const {
    paymentMethods,
    savedCards,
    isLoading,
    isDeleting,
    isSettingDefault,
    deletePaymentMethodById,
    setDefaultPaymentMethodById,
  } = usePatientProfilePaymentMethods({ enabled })

  const [pendingDelete, setPendingDelete] =
    useState<PendingPaymentDelete | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    setPendingDelete(null)
    setShowDeleteModal(false)
  }, [paymentMethods, savedCards])

  const handleRequestDeletePaymentMethod = useCallback(
    (item: AccountSummaryPaymentMethodItem): void => {
      setPendingDelete({ kind: 'payment', item })
      setShowDeleteModal(true)
    },
    [],
  )

  const handleRequestDeleteSavedCard = useCallback(
    (item: AccountSummarySavedCardItem): void => {
      setPendingDelete({ kind: 'card', item })
      setShowDeleteModal(true)
    },
    [],
  )

  const handleCloseDeleteModal = useCallback((): void => {
    setShowDeleteModal(false)
    setPendingDelete(null)
  }, [])

  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!pendingDelete || isDeleting) return

    const success = await deletePaymentMethodById(pendingDelete.item.id)
    if (success) {
      setShowDeleteModal(false)
      setPendingDelete(null)
    }
  }, [deletePaymentMethodById, isDeleting, pendingDelete])

  const handleSetDefaultPaymentMethod = useCallback(
    (item: AccountSummaryPaymentMethodItem): void => {
      void setDefaultPaymentMethodById(item.id)
    },
    [setDefaultPaymentMethodById],
  )

  const handleSetDefaultSavedCard = useCallback(
    (item: AccountSummarySavedCardItem): void => {
      void setDefaultPaymentMethodById(item.id)
    },
    [setDefaultPaymentMethodById],
  )

  const deleteModalTitle =
    pendingDelete?.kind === 'payment'
      ? 'Remove payment method'
      : pendingDelete?.kind === 'card'
        ? 'Remove saved card'
        : 'Confirm deletion'

  const deleteModalBody =
    pendingDelete?.kind === 'payment'
      ? `Remove account ending in ${pendingDelete.item.lastFour} from saved payment methods? This cannot be undone.`
      : pendingDelete?.kind === 'card'
        ? `Remove card ending in ${pendingDelete.item.lastFour} from saved cards? This cannot be undone.`
        : undefined

  const isPaymentNetworkBusy = useMemo(
    () => isLoading || isDeleting || isSettingDefault,
    [isLoading, isDeleting, isSettingDefault],
  )

  return {
    paymentMethods,
    savedCards,
    isLoading,
    isDeleting,
    isSettingDefault,
    isPaymentNetworkBusy,
    showDeleteModal,
    deleteModalTitle,
    deleteModalBody,
    handleRequestDeletePaymentMethod,
    handleRequestDeleteSavedCard,
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleSetDefaultPaymentMethod,
    handleSetDefaultSavedCard,
  }
}

export default usePatientProfilePaymentMethodActions
